import React from "react"
import {renderRectangularLidOutline} from "./renderer";
import {createLidGeometry, LidGeometry} from "./lidGeometry";
import {renderDashedLine} from "../../renderFunctions";
import {ConventionalManhole} from "./ConventionalManhole";
import {CircularBlockOut as CircularBlockOutType, Plan, RectangularBlockOut, Side} from "types";
import {getCombinedLidSize, getLidSize} from "../../../../domain/lid";
import {Group} from "react-konva";

interface ConventionalLidsProps {
    plan: Plan;
    scale: number;
    side: Side;
}

interface ConventionalLidProps {
    plan: Plan;
    manholes: Plan['lid']['manholes'];
    scale: number;
    side: Side;
}

const ConventionalLid: React.FC<ConventionalLidProps> = ({plan, manholes, scale, side}) => {
    const lidGeometry = createLidGeometry(plan, manholes, scale, side)
    const screenXOffset = () => {
        const lidSize = getLidSize(plan, manholes)
        const combinedLidSize = getCombinedLidSize(plan)
        const realOffset =
            side === 'front' ? lidSize.left - combinedLidSize.left
                : side === 'back' ? combinedLidSize.right - lidSize.right
                    : side === 'left' ? combinedLidSize.back - lidSize.back
                        : side === 'right' ? lidSize.front - combinedLidSize.front
                            : 0

        return realOffset * scale
    }

    return (
        <Group x={screenXOffset()} y={0}>
            {manholes.map((m, i) =>
                <ConventionalManhole
                    plan={plan}
                    lidGeometry={lidGeometry}
                    scale={scale}
                    manhole={m}
                    side={side}
                    key={'manhole_' + i}
                />
            )}
            {plan.lid.blockOuts?.map((b, i) =>
                <BlockOut plan={plan} blockOut={b} lidGeometry={lidGeometry} side={side} scale={scale}
                          key={'block_out_' + i}/>)}
            {plan.lid.circularBlockOuts?.map((b, i) =>
                <CircularBlockOut plan={plan} blockOut={b} lidGeometry={lidGeometry} side={side} scale={scale}
                                  key={'circular_block_out_' + i}/>
            )}
        </Group>
    )
}

const BlockOut: React.FC<{
    plan: Plan,
    blockOut: RectangularBlockOut,
    lidGeometry: LidGeometry,
    side: Side,
    scale: number
}> = ({plan, blockOut, lidGeometry, side, scale}) => {
    const getRectLeft = () =>
        lidGeometry.clearLeft
        + (side === 'left' ? plan.width - blockOut.height - blockOut.y
                : side === 'right' ? blockOut.y
                    : side === 'front' ? blockOut.x
                        : side === 'back' ? plan.length - blockOut.width - blockOut.x
                            : 0
        ) * scale
    const getRectWidth = () =>
        side === 'left' || side === 'right'
            ? blockOut.height * scale
            : blockOut.width * scale
    const getRectRight = () => getRectLeft() + getRectWidth()
    const getLeft = () => Math.max(getRectLeft(), 0)
    const getRight = () => Math.min(getRectRight(), lidGeometry.clearRight)

    return (
        <React.Fragment>
            {renderDashedLine([getLeft(), lidGeometry.top, getLeft(), lidGeometry.bottom])}
            {renderDashedLine([getRight(), lidGeometry.top, getRight(), lidGeometry.bottom])}
        </React.Fragment>
    )
}
const CircularBlockOut: React.FC<{
    plan: Plan,
    blockOut: CircularBlockOutType,
    lidGeometry: LidGeometry,
    side: Side,
    scale: number
}> = ({plan, blockOut, lidGeometry, side, scale}) => {
    const getRadius = () => blockOut.outerDiameter / 2 * scale
    const getX = () =>
        (side === 'left' ? plan.width + plan.wallWidth - blockOut.y
                : side === 'right' ? plan.wallWidth + blockOut.y
                    : side === 'front' ? blockOut.x + plan.wallWidth
                        : side === 'back' ? plan.length + plan.wallWidth - blockOut.x
                            : 0
        ) * scale
    const getLeft = () => (getX() - getRadius())
    const getRight = () => (getX() + getRadius())

    return (
        <React.Fragment>
            {renderDashedLine([getLeft(), lidGeometry.top, getLeft(), lidGeometry.bottom])}
            {renderDashedLine([getRight(), lidGeometry.top, getRight(), lidGeometry.bottom])}
        </React.Fragment>
    )
}

export const ConventionalLids: React.FC<ConventionalLidsProps> = ({plan, scale, side}) => {
    if (plan.lid.manholes.length < 1) {
        const lidGeometry = createLidGeometry(plan, [], scale, side)

        return (

            <React.Fragment>

                {plan.lid.blockOuts?.map((b, i) =>
                    <BlockOut plan={plan} blockOut={b} lidGeometry={lidGeometry} side={side} scale={scale}
                              key={'block_out_' + i}/>)}
                {plan.lid.circularBlockOuts?.map((b, i) =>
                    <CircularBlockOut plan={plan} blockOut={b} lidGeometry={lidGeometry} side={side} scale={scale}
                                      key={'circular_block_out_' + i}/>
                )}
                {renderRectangularLidOutline(lidGeometry)}
            </React.Fragment>
        );


    }
    if (!!plan.intermediateCeiling) {
        return (
            <React.Fragment>
                {
                    plan.lid.manholes.map((m, i) =>
                        <ConventionalLid
                            plan={plan}
                            manholes={[m]}
                            scale={scale}
                            side={side}
                            key={'conventional_lid_' + i}
                        />
                    )
                }
            </React.Fragment>
        )
    }

    return <ConventionalLid plan={plan} manholes={plan.lid.manholes} scale={scale} side={side}/>

}

